<template>
  <div class="congruence-similarity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg viewBox="-200 -200 400 360" class="canvas-svg">
          <!-- Triangle ABC -->
          <polygon :points="`${ptA.x},${ptA.y} ${ptB.x},${ptB.y} ${ptC.x},${ptC.y}`" fill="rgba(13, 104, 255, 0.15)" stroke="#0D68FF" stroke-width="2" />
          <text :x="ptA.x - 10" :y="ptA.y - 10" fill="#8ba1ff" font-weight="bold">A</text>
          <text :x="ptB.x - 15" :y="ptB.y + 15" fill="#8ba1ff" font-weight="bold">B</text>
          <text :x="ptC.x + 10" :y="ptC.y + 15" fill="#8ba1ff" font-weight="bold">C</text>

          <!-- Extended sides and additional construction lines based on the center type -->
          <g stroke="rgba(255,255,255,0.2)" stroke-width="1.5" stroke-dasharray="3,3">
             <template v-if="centerType === 'centroid'">
                 <!-- Medians -->
                 <line :x1="ptA.x" :y1="ptA.y" :x2="(ptB.x+ptC.x)/2" :y2="(ptB.y+ptC.y)/2" stroke="#2AF598" />
                 <line :x1="ptB.x" :y1="ptB.y" :x2="(ptA.x+ptC.x)/2" :y2="(ptA.y+ptC.y)/2" stroke="#2AF598" />
                 <line :x1="ptC.x" :y1="ptC.y" :x2="(ptA.x+ptB.x)/2" :y2="(ptA.y+ptB.y)/2" stroke="#2AF598" />
             </template>
             <template v-else-if="centerType === 'orthocenter'">
                 <!-- Altitudes -->
                 <line :x1="ptA.x" :y1="ptA.y" :x2="altA.x" :y2="altA.y" stroke="#facc15" />
                 <line :x1="ptB.x" :y1="ptB.y" :x2="altB.x" :y2="altB.y" stroke="#facc15" />
                 <line :x1="ptC.x" :y1="ptC.y" :x2="altC.x" :y2="altC.y" stroke="#facc15" />
                 <!-- Extension lines for obtuse -->
                 <line :x1="altA.x" :y1="altA.y" :x2="centerPt.x" :y2="centerPt.y" stroke="#facc15" stroke-dasharray="1" />
                 <line :x1="altB.x" :y1="altB.y" :x2="centerPt.x" :y2="centerPt.y" stroke="#facc15" stroke-dasharray="1" />
                 <line :x1="altC.x" :y1="altC.y" :x2="centerPt.x" :y2="centerPt.y" stroke="#facc15" stroke-dasharray="1" />
             </template>
             <template v-else-if="centerType === 'circumcenter'">
                 <!-- Perpendicular bisectors -->
                 <line :x1="(ptB.x+ptC.x)/2" :y1="(ptB.y+ptC.y)/2" :x2="centerPt.x" :y2="centerPt.y" stroke="#38bdf8" />
                 <line :x1="(ptA.x+ptC.x)/2" :y1="(ptA.y+ptC.y)/2" :x2="centerPt.x" :y2="centerPt.y" stroke="#38bdf8" />
                 <line :x1="(ptA.x+ptB.x)/2" :y1="(ptA.y+ptB.y)/2" :x2="centerPt.x" :y2="centerPt.y" stroke="#38bdf8" />
                 <!-- Circumcircle -->
                 <circle :cx="centerPt.x" :cy="centerPt.y" :r="circumRadius" fill="none" stroke="#38bdf8" stroke-dasharray="0" />
             </template>
             <template v-else-if="centerType === 'incenter'">
                 <!-- Angle bisectors -->
                 <line :x1="ptA.x" :y1="ptA.y" :x2="centerPt.x" :y2="centerPt.y" stroke="#f43f5e" />
                 <line :x1="ptB.x" :y1="ptB.y" :x2="centerPt.x" :y2="centerPt.y" stroke="#f43f5e" />
                 <line :x1="ptC.x" :y1="ptC.y" :x2="centerPt.x" :y2="centerPt.y" stroke="#f43f5e" />
                 <!-- Incircle -->
                 <circle :cx="centerPt.x" :cy="centerPt.y" :r="inRadius" fill="none" stroke="#f43f5e" stroke-dasharray="0" />
             </template>
          </g>

          <!-- Center Point -->
          <circle :cx="centerPt.x" :cy="centerPt.y" r="5" fill="#ffffff" />
          <text :x="centerPt.x + 8" :y="centerPt.y + 12" fill="#ffffff" font-weight="bold">🎯 {{ centerName }}</text>

        </svg>

        <div class="hud-center">
          <div class="status-badge con">
             当前显示: {{ centerNameDesc }}
          </div>
        </div>
      </div>

      <div class="compact-controls">
         <div class="control-card glass">
            <div class="control-vertical-group">
               <div class="param-row">
                  <div class="s-item" style="grid-column: span 2;">
                     <label>游动顶点 C (X)</label>
                     <el-slider v-model="ptC_x" :min="-150" :max="150" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item" style="grid-column: span 2;">
                     <label>游动顶点 C (Y)</label>
                     <el-slider v-model="ptC_y" :min="-180" :max="50" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item" style="grid-column: span 4;">
                     <label>选择特殊点 (五心)</label>
                     <el-radio-group v-model="centerType" size="small">
                        <el-radio-button label="centroid">重心</el-radio-button>
                        <el-radio-button label="orthocenter">垂心</el-radio-button>
                        <el-radio-button label="circumcenter">外心</el-radio-button>
                        <el-radio-button label="incenter">内心</el-radio-button>
                     </el-radio-group>
                  </div>
               </div>
            </div>
         </div>
         <div class="discovery-card-compact" v-latex>
            <i>🔍</i>
            <p><strong>概念解读：</strong><br/>重心：三条中线的交点 (2:1法则)；<br/>垂心：三条高线的交点 (可能在形外)；<br/>外心：三条中垂线的交点 (外接圆圆心)；<br/>内心：三个内角平分线的交点 (内切圆圆心)。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'TriangleCentersViz',
  data() {
    return {
      ptA: { x: -100, y: 100 },
      ptB: { x: 120, y: 100 },
      ptC_x: 60,
      ptC_y: -80,
      centerType: 'centroid'
    }
  },
  computed: {
    ptC() { return { x: this.ptC_x, y: this.ptC_y }; },
    lens() {
      return {
        a: Math.hypot(this.ptB.x - this.ptC.x, this.ptB.y - this.ptC.y),
        b: Math.hypot(this.ptA.x - this.ptC.x, this.ptA.y - this.ptC.y),
        c: Math.hypot(this.ptA.x - this.ptB.x, this.ptA.y - this.ptB.y)
      };
    },
    centerName() {
       const m = {'centroid': '重心', 'orthocenter': '垂心', 'circumcenter': '外心', 'incenter': '内心'};
       return m[this.centerType];
    },
    centerNameDesc() {
       const m = {'centroid': '中线交点', 'orthocenter': '高线交点', 'circumcenter': '中垂线交点定外接圆', 'incenter': '角平分线交点定内切圆'};
       return this.centerName + ' - ' + m[this.centerType];
    },
    centerPt() {
       let a = this.ptA, b = this.ptB, c = this.ptC, l = this.lens;
       if (this.centerType === 'centroid') {
          return { x: (a.x+b.x+c.x)/3, y: (a.y+b.y+c.y)/3 };
       }
       if (this.centerType === 'incenter') {
          let per = l.a + l.b + l.c;
          return { x: (l.a*a.x + l.b*b.x + l.c*c.x)/per, y: (l.a*a.y + l.b*b.y + l.c*c.y)/per };
       }
       if (this.centerType === 'circumcenter') {
          let d = 2*(a.x*(b.y-c.y) + b.x*(c.y-a.y) + c.x*(a.y-b.y));
          if(Math.abs(d) < 0.001) return {x:0, y:0};
          let a2 = a.x*a.x + a.y*a.y, b2 = b.x*b.x + b.y*b.y, c2 = c.x*c.x + c.y*c.y;
          return {
             x: (a2*(b.y-c.y) + b2*(c.y-a.y) + c2*(a.y-b.y))/d,
             y: (a2*(c.x-b.x) + b2*(a.x-c.x) + c2*(b.x-a.x))/d
          };
       }
       if (this.centerType === 'orthocenter') {
          // Orthocenter H: H + Centroid = 3 * Centroid (Euler line H, G, O: G divides OH in 1:2)
          // So H = 3G - 2O (Actually Euler line OH, G is 1/3 from O to H, so G = (2O+H)/3 => H = 3G - 2O)
          let G = { x: (a.x+b.x+c.x)/3, y: (a.y+b.y+c.y)/3 };
          let d = 2*(a.x*(b.y-c.y) + b.x*(c.y-a.y) + c.x*(a.y-b.y));
          if(Math.abs(d) < 0.001) return {x:0, y:0};
          let a2 = a.x*a.x + a.y*a.y, b2 = b.x*b.x + b.y*b.y, c2 = c.x*c.x + c.y*c.y;
          let O = {
             x: (a2*(b.y-c.y) + b2*(c.y-a.y) + c2*(a.y-b.y))/d,
             y: (a2*(c.x-b.x) + b2*(a.x-c.x) + c2*(b.x-a.x))/d
          };
          return { x: 3*G.x - 2*O.x, y: 3*G.y - 2*O.y };
       }
       return {x: 0, y: 0};
    },
    circumRadius() {
       return Math.hypot(this.ptA.x - this.centerPt.x, this.ptA.y - this.centerPt.y);
    },
    inRadius() {
       // Area = sqrt(s(s-a)(s-b)(s-c)), r = Area / s
       let l = this.lens;
       let s = (l.a + l.b + l.c)/2;
       let area = Math.sqrt(s*(s-l.a)*(s-l.b)*(s-l.c));
       return area / s;
    },
    altA() { return this.getFoot(this.ptA, this.ptB, this.ptC); },
    altB() { return this.getFoot(this.ptB, this.ptA, this.ptC); },
    altC() { return this.getFoot(this.ptC, this.ptA, this.ptB); }
  },
  methods: {
    getFoot(p, a, b) {
      let dx = b.x - a.x, dy = b.y - a.y;
      if (dx === 0 && dy === 0) return p;
      let u = ((p.x - a.x)*dx + (p.y - a.y)*dy) / (dx*dx + dy*dy);
      return { x: a.x + u*dx, y: a.y + u*dy };
    }
  }
}
</script>

<style scoped lang="scss">
.congruence-similarity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .canvas-svg { width: 100%; height: 100%; } .hud-center { position: absolute; top: 15px; width: 100%; text-align: center; display: flex; justify-content: center; .status-badge { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); padding: 4px 16px; border-radius: 20px; color: #fff; font-weight: bold; font-size: 13px; backdrop-filter: blur(4px); &.con { background: rgba(56,189,248,0.2); border-color: #38bdf8; color: #bae6fd; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
